import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { MainAction } from "@/components/MainAction";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { formatUzs } from "@/lib/format";
import { PayBadge } from "@/components/PayBadge";
import { ApiError, fetchMarketingConsent, placeOrder, validatePromo } from "@/lib/api";
import { promoErrorText } from "@/lib/promoText";
import type { DeliveryMethod, PaymentMethod, PromoPreview } from "@/types";
import { useBackButton } from "@/lib/hooks";
import { useConfig } from "@/lib/useConfig";
import { DeliveryPicker } from "@/components/DeliveryPicker";
import { FaqLink } from "@/pages/FaqPage";
import { checkGiftCard, fetchAddresses, saveAddress, type SavedAddress } from "@/lib/api";
import { canRequestPhone, isInsideTelegram, requestPhone } from "@/lib/telegram";
import { goal, track } from "@/lib/analytics";
import { haptic, openExternalLink } from "@/lib/telegram";


export function CheckoutPage() {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  const { lines, subtotalUzs, clear, promoCode, setPromoCode, bundleLines } = useCart();
  const { delivery: deliveryCfg } = useConfig();
  const hasItems = lines.length > 0 || bundleLines.length > 0;

  const [delivery, setDelivery] = useState<DeliveryMethod>("pickup");
  const [payment, setPayment] = useState<PaymentMethod>("payme");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  // Saved addresses, delivery slot, gift and certificate
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [saveAddr, setSaveAddr] = useState(false);
  const [addrLabel, setAddrLabel] = useState("");
  const [when, setWhen] = useState<{ date?: string; slot?: string }>({});
  const [isGift, setIsGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [giftFrom, setGiftFrom] = useState("");
  const [certInput, setCertInput] = useState("");
  const [cert, setCert] = useState<{ code: string; balanceUzs: number } | null>(null);
  const [certError, setCertError] = useState<string | null>(null);
  const applyAddress = (a: SavedAddress) => {
    setAddressId(a.id);
    setFullName(a.fullName);
    setPhone(a.phone);
    setAddress(a.address);
    if (a.comment) setComment(a.comment);
  };
  useEffect(() => {
    track("checkout");
    fetchAddresses()
      .then((list) => {
        setAddresses(list);
        // Prefill the most recently used address.
        if (list[0]) applyAddress(list[0]);
      })
      .catch(() => {});
  }, []);
  const [accepted, setAccepted] = useState(false);
  // News opt-in: unchecked by default, hidden if the customer already agreed.
  const [marketing, setMarketing] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(true);
  useEffect(() => {
    fetchMarketingConsent().then(setAlreadySubscribed).catch(() => setAlreadySubscribed(true));
  }, []);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PromoPreview | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  useBackButton(goBack);

  // Cash is only for pickup — switch away from it if delivery is chosen.
  useEffect(() => {
    if (delivery !== "pickup" && payment === "cash_on_pickup") setPayment("payme");
  }, [delivery, payment]);

  // Re-check the promo against the REAL delivery method.
  useEffect(() => {
    if (!promoCode || !hasItems) {
      setPreview(null);
      setPromoError(null);
      return;
    }
    let cancelled = false;
    validatePromo(promoCode, lines, delivery, bundleLines)
      .then((p) => !cancelled && (setPreview(p), setPromoError(null)))
      .catch((err) => !cancelled && (setPreview(null), setPromoError(promoErrorText(t, locale, err))));
    return () => {
      cancelled = true;
    };
  }, [promoCode, lines, bundleLines, delivery, t, locale]);

  // Same rule as the server: free from the threshold set in the admin panel.
  const freeByThreshold = deliveryCfg.freeThresholdUzs > 0 && subtotalUzs >= deliveryCfg.freeThresholdUzs;
  const deliveryFee = delivery === "yandex" && !freeByThreshold ? deliveryCfg.yandexFeeUzs : 0;
  const discount = preview?.discountUzs ?? 0;
  const beforeCert = Math.max(0, subtotalUzs + deliveryFee - discount);
  const certUsed = cert ? Math.min(cert.balanceUzs, beforeCert) : 0;
  const total = beforeCert - certUsed;

  const contactValid = !!fullName.trim() && phone.replace(/\D/g, "").length >= 9 && (delivery === "pickup" || (isGift && delivery === "yandex") || !!address.trim());
  const canSubmit = hasItems && contactValid && accepted && !promoError && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setShowErrors(true);
      haptic("error");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    // Regular browser (desktop site): open the payment tab right on the click — browsers block
    // tabs opened after an await. Inside Telegram nothing changes (Telegram opens the link).
    const payTab = !isInsideTelegram() && payment !== "cash_on_pickup" ? window.open("about:blank", "_blank") : null;
    try {
      const { order, checkoutUrl, giftLink } = await placeOrder({
        lines,
        deliveryMethod: delivery,
        paymentMethod: payment,
        contact: { fullName, phone, address: delivery === "pickup" ? undefined : address, comment: comment || undefined },
        promoCode: promoCode ?? undefined,
        acceptedTerms: accepted,
        deliveryDate: !isGift && delivery === "yandex" && when.slot ? when.date : undefined,
        deliverySlot: !isGift && delivery === "yandex" ? when.slot : undefined,
        giftCardCode: cert?.code,
        gift: isGift && delivery === "yandex" ? { message: giftMessage.trim() || undefined, fromName: giftFrom.trim() || undefined } : undefined,
        bundles: bundleLines,
        marketingConsent: marketing || undefined,
      });
      haptic("success");
      if (saveAddr && addressId === null && delivery === "yandex" && !isGift) {
        saveAddress({ label: addrLabel.trim() || t("addresses.defaultLabel"), fullName: fullName.trim(), phone: phone.trim(), address: address.trim(), comment: comment.trim() || undefined }).catch(() => {});
      }
      clear();
      goal("order_created");
      if (giftLink) {
        try {
          sessionStorage.setItem(`kms.gift.${order.id}`, giftLink);
        } catch {
          /* the order page also has the link */
        }
      }
      if (checkoutUrl) {
        if (payTab && !payTab.closed) payTab.location.href = checkoutUrl;
        else openExternalLink(checkoutUrl);
      } else {
        payTab?.close();
      }
      navigate({ name: "orderConfirmation", orderId: order.id });
    } catch (err) {
      payTab?.close();
      haptic("error");
      setSubmitError(err instanceof ApiError && err.code?.startsWith("PROMO_") ? promoErrorText(t, locale, err) : err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TopBar title={t("checkout.title")} onBack={goBack} showCart={false} />
      <main className="page page--no-nav">
        <div className="section-heading" style={{ fontSize: 15 }}>
          <span className="diamond" />
          {t("checkout.deliveryMethod")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: "var(--space-5)" }}>
          <OptionCard
            active={delivery === "pickup"}
            title={t("checkout.pickup")}
            detail={`${t("checkout.pickupAddress")} · ${t("checkout.pickupHours")} · ${t("checkout.pickupReadyIn")}`}
            onSelect={() => setDelivery("pickup")}
          />
          <OptionCard
            active={delivery === "yandex"}
            title={t("checkout.yandex")}
            detail={freeByThreshold ? t("checkout.yandexFree") : t("checkout.yandexFeeAmount", { amount: formatUzs(deliveryCfg.yandexFeeUzs, locale) })}
            onSelect={() => setDelivery("yandex")}
          />
          <OptionCard active={false} disabled title={t("checkout.bts")} detail={t("checkout.btsSoon")} onSelect={() => {}} />
        </div>

        <div className="section-heading" style={{ fontSize: 15 }}>
          <span className="diamond" />
          {t("checkout.contactTitle")}
        </div>
        <div className="field">
          <label htmlFor="co-name">{t("checkout.fullName")}</label>
          <input id="co-name" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} aria-invalid={showErrors && !fullName.trim()} />
        </div>
        {canRequestPhone() && (
          <button
            className="contact-btn"
            onClick={async () => {
              const p = await requestPhone();
              if (p) {
                haptic("success");
                setPhone(p);
              }
            }}
          >
            {t("growth.sharePhone")}
          </button>
        )}
        <div className="field">
          <label htmlFor="co-phone">{t("checkout.phone")}</label>
          <input
            id="co-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s()-]/g, "").slice(0, 20))}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+998 90 123 45 67"
            aria-invalid={showErrors && phone.replace(/\D/g, "").length < 9}
          />
          {showErrors && phone.replace(/\D/g, "").length < 9 && <span className="field__error">{t("inquiry.phoneInvalid")}</span>}
        </div>

        {delivery === "yandex" && (
          <label className="gift-toggle">
            <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} />
            <span>
              <b>🎁 {t("gift.checkoutTitle")}</b>
              <small>{t("gift.checkoutHint")}</small>
            </span>
          </label>
        )}
        {isGift && delivery === "yandex" && (
          <>
            <div className="field">
              <label>{t("gift.fromLabel")}</label>
              <input value={giftFrom} maxLength={80} onChange={(e) => setGiftFrom(e.target.value)} placeholder={fullName} />
            </div>
            <div className="field">
              <label>{t("gift.messageLabel")}</label>
              <textarea rows={2} maxLength={300} value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} />
            </div>
          </>
        )}

        {delivery !== "pickup" && !(isGift && delivery === "yandex") && (
          <>
            {addresses.length > 0 && (
              <div className="segmented" style={{ marginBottom: 12 }}>
                {addresses.map((a) => (
                  <button key={a.id} className="chip" data-active={addressId === a.id} onClick={() => applyAddress(a)}>
                    {a.label}
                  </button>
                ))}
                <button className="chip" data-active={addressId === null} onClick={() => { setAddressId(null); setAddress(""); }}>
                  + {t("addresses.new")}
                </button>
              </div>
            )}
            <div className="field">
              <label htmlFor="co-address">{t("checkout.address")}</label>
              <input id="co-address" autoComplete="street-address" value={address} onChange={(e) => { setAddress(e.target.value); setAddressId(null); }} aria-invalid={showErrors && !address.trim()} />
            </div>
            {addressId === null && address.trim().length > 3 && (
              <label className="consent" style={{ margin: "-6px 0 14px" }}>
                <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} />
                <span>
                  {t("addresses.save")}
                  {saveAddr && <input className="inline-input" value={addrLabel} maxLength={30} onChange={(e) => setAddrLabel(e.target.value)} placeholder={t("addresses.labelPh")} />}
                </span>
              </label>
            )}
            <DeliveryPicker value={when} onChange={setWhen} />
          </>
        )}

        <div className="field">
          <label htmlFor="co-comment">{t("checkout.comment")}</label>
          <textarea id="co-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>

        <div className="section-heading" style={{ fontSize: 15 }}>
          <span className="diamond" />
          {t("checkout.paymentMethod")}
        </div>
        <div className="pay-options">
          {(["payme", "click", "uzum"] as PaymentMethod[]).map((method) => (
            <OptionCard
              key={method}
              active={payment === method}
              icon={<PayBadge method={method} />}
              title={method === "payme" ? "Payme" : method === "click" ? "Click" : "Uzum"}
              detail={t("checkout.payOnline")}
              onSelect={() => setPayment(method)}
            />
          ))}
          {delivery === "pickup" && (
            <OptionCard active={payment === "cash_on_pickup"} icon={<PayBadge method="cash_on_pickup" />} title={t("checkout.cashOnPickup")} detail={t("checkout.cashDetail")} onSelect={() => setPayment("cash_on_pickup")} />
          )}
        </div>

        <div className="hr" />
        <div className="total-row">
          <span>{t("cart.subtotal")}</span>
          <span>{formatUzs(subtotalUzs, locale)}</span>
        </div>
        {deliveryFee > 0 && (
          <div className="total-row">
            <span>{t("checkout.deliveryLine")}</span>
            <span>{formatUzs(deliveryFee, locale)}</span>
          </div>
        )}
        {discount > 0 && preview && (
          <div className="total-row total-row--discount">
            <span>{t("checkout.discountLine", { code: preview.code })}</span>
            <span>−{formatUzs(discount, locale)}</span>
          </div>
        )}
        {certUsed > 0 && (
          <div className="total-row total-row--discount">
            <span>{t("cert.line", { code: cert!.code })}</span>
            <span>−{formatUzs(certUsed, locale)}</span>
          </div>
        )}
        {promoError && (
          <div className="promo-applied promo-applied--error" style={{ marginTop: 8 }}>
            <span>{promoError}</span>
            <button className="btn btn--danger-text" onClick={() => setPromoCode(null)}>
              {t("cart.promoRemove")}
            </button>
          </div>
        )}
        <div className="total-row total-row--grand">
          <span>{t("cart.total")}</span>
          <span>{formatUzs(total, locale)}</span>
        </div>

        <FaqLink category="order" title={t("faq.checkoutLink")} hint={t("faq.checkoutHint")} />

        {!cert ? (
          <div className="cert-row">
            <input className="promo-input" placeholder={t("cert.placeholder")} value={certInput} onChange={(e) => setCertInput(e.target.value.toUpperCase())} />
            <button
              className="btn btn--ghost"
              disabled={certInput.trim().length < 4}
              onClick={async () => {
                setCertError(null);
                try {
                  const r = await checkGiftCard(certInput.trim());
                  haptic("success");
                  setCert({ code: r.code, balanceUzs: r.balanceUzs });
                } catch (err) {
                  setCertError(err instanceof ApiError ? err.message : t("common.error"));
                }
              }}
            >
              {t("cert.apply")}
            </button>
          </div>
        ) : (
          <div className="promo-applied" style={{ marginTop: 12 }}>
            <span>{t("cert.applied", { balance: formatUzs(cert.balanceUzs, locale) })}</span>
            <button className="btn btn--danger-text" onClick={() => setCert(null)}>
              {t("cart.promoRemove")}
            </button>
          </div>
        )}
        {certError && <div className="field__error">{certError}</div>}

        <label className={`consent ${showErrors && !accepted ? "consent--error" : ""}`}>
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            {t("checkout.consentBefore")}
            <a
              href="#/page/offer"
              onClick={(e) => {
                e.preventDefault();
                navigate({ name: "post", slug: "offer" });
              }}
            >
              {t("checkout.offerLink")}
            </a>
            {t("checkout.consentMiddle")}
            <a
              href="#/page/privacy"
              onClick={(e) => {
                e.preventDefault();
                navigate({ name: "post", slug: "privacy" });
              }}
            >
              {t("checkout.privacyLink")}
            </a>
            {t("checkout.consentAfter")}
          </span>
        </label>
        {showErrors && !accepted && <div className="field__error">{t("checkout.consentRequired")}</div>}
        {!alreadySubscribed && (
          <label className="consent" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
            <span>{t("marketing.checkoutOptIn")}</span>
          </label>
        )}

        {submitError && <div className="form-error-banner">{submitError}</div>}
        <MainAction text={t("checkout.placeOrder")} onClick={handleSubmit} loading={submitting} disabled={!hasItems} />
      </main>
    </>
  );
}

function OptionCard({ active, disabled, title, detail, icon, onSelect }: { active: boolean; disabled?: boolean; title: string; detail?: string; icon?: React.ReactNode; onSelect: () => void }) {
  return (
    <button
      className="card option-card"
      data-active={active}
      disabled={disabled}
      onClick={() => {
        haptic("selection");
        onSelect();
      }}
    >
      <span className="option-card__radio" aria-hidden="true" />
      {icon}
      <span style={{ flex: 1, textAlign: "left" }}>
        <span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>{title}</span>
        {detail && <span style={{ display: "block", fontSize: 12.5, color: "var(--color-ink-soft)", marginTop: 3 }}>{detail}</span>}
      </span>
    </button>
  );
}
